import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  NavLink,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { CalendarDays, Menu, X } from "lucide-react";

import "./styles/themes.css";
import "./styles/App.css";

import About from "./pages/about";
import HelpPage from "./pages/helpPage";
import ContactPage from "./pages/contactPage";
import Counter from "./pages/counter";

import LoginPage from "./pages/projLoginPage";
import HomePage from "./pages/homePage";
import SelectGameModes from "./pages/selectGameMode";

import SpotWordGame from "./pages/spotWordGame";
import Mcq10Game from "./pages/mcq10Game";
import ComprehensionGame from "./pages/comprehensionGame";
import VocabularyMatchingGame from "./pages/vocabMatchingGame";
import SynonymsGame from "./pages/synonymsGame";
import AntonymsGame from "./pages/antonymsGame";
import ExtremeGame from "./pages/extremeGame";
import RankedGame from "./pages/rankedGame";

import LevelSelector from "./pages/levelSelector";
import BookSelectorPage from "./pages/bookSelector";
import BookBasedComprehensionGame from "./pages/bookBasedComprehensionGame";
import BookReadingPage from "./pages/bookReading";
import ChapterReadingPage from "./pages/chapterReadingPage";
import OwlLotterySystem from "./pages/owlCollectingPage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminReportPage from "./pages/adminReportPage";
import AdminUserReportPage from "./pages/adminUserReportPage";
import MyReportPage from "./pages/myReportPage";
import OwlNestPage from "./pages/owlNest";
import StatisticsPage from "./pages/statisticsPage";
import OwlCollectionTest from "./pages/owlCollectionTest";

import CategoryGamesPage from "./pages/categoryGamesPage";
import CalendarPage from "./pages/calendarPage";
import ResultsPage from "./pages/resultsPage";
import ResultDetailPage from "./pages/resultDetailPage";
import ProtectedRoute from "./protectedRoute";
import RegistrationConfirmedPage from "./pages/registrationConfirmedPage";
import ActivateAccountPage from "./pages/ActivateAccountPage";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import RegisterPage from "./pages/RegisterPage";
import CookieBanner from "./pages/CookieBanner";
import ThemeSelector, { loadTheme, applyTheme } from "./pages/ThemeSelector";
import apiFetch, {
  tryRestoreSession,
  setAccessToken,
  clearAccessToken,
} from "./apiFetch";
import TestModesPage from "./pages/testModesPage";
import VocabTestSetup from "./pages/vocabTestSetup";
import VocabTestGame from "./pages/vocabTestGame";
import VocabPrintPage from "./pages/vocabPrintPage";
import {
  PrivacyPolicyPage,
  TermsPage,
  CookiePolicyPage,
  SafeguardingPage,
  AccessibilityPage,
} from "./pages/legalPages";
import LandingPage from "./pages/landingPage";
import { ErrorBoundary, ErrorPage } from "./pages/errorPage";
import { getSession, clearSession } from "./session";
import { DEV_MODE, API_BASE } from "./config";

// Apply saved theme before first render
loadTheme();

// Attempt to restore an access token from the httpOnly refresh cookie on page load
tryRestoreSession().then((info) => {
  if (info) {
    // isAdmin from the JWT — write it to localStorage so legacy pages can read it
    localStorage.setItem("isAdmin", info.isAdmin ? "1" : "0");
  }
});

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const RootRedirect = () => {
  const session = getSession();
  return <Navigate to={session ? "/home" : "/projLoginPage"} replace />;
};

const GameRoute = ({ element }) => {
  const location = useLocation();
  if (!DEV_MODE && !location.state?.fromGameSelect) {
    return <Navigate to="/selectGameMode" replace />;
  }
  return element;
};

const Footer = () => (
  <footer className="site-footer">
    <p>
      © {new Date().getFullYear()} Owllet Reading. Book texts and book images
      are sourced from the public domain. All owllet character artwork is
      original and copyright © Owllet Reading. Unauthorised reproduction or
      distribution is prohibited.
    </p>
    <p className="site-footer-links">
      <a href="/privacy-policy">Privacy Policy</a>
      <span className="site-footer-sep">·</span>
      <a href="/terms">Terms &amp; Conditions</a>
      <span className="site-footer-sep">·</span>
      <a href="/cookie-policy">Cookie Policy</a>
      <span className="site-footer-sep">·</span>
      <a href="/safeguarding">Safeguarding</a>
      <span className="site-footer-sep">·</span>
      <a href="/accessibility">Accessibility</a>
    </p>
  </footer>
);

const NavBar = () => {
  const navigate = useNavigate();
  const session = getSession();
  const [isAdmin, setIsAdmin] = useState(session?.isAdmin ?? false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!session) return;
    if (localStorage.getItem("isAdmin") !== "1") {
      apiFetch(`/api/admin/check?adminUserId=${session.userId}`)
        .then((r) => r.json())
        .then((d) => {
          if (d.isAdmin) {
            localStorage.setItem("isAdmin", "1");
            setIsAdmin(true);
          }
        })
        .catch(() => {});
    }
  }, []); // eslint-disable-line

  const handleSignOut = async () => {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      /* ignore */
    }
    clearAccessToken();
    clearSession();
    applyTheme("midnight");
    setMenuOpen(false);
    navigate("/projLoginPage");
  };

  const closeMenu = () => setMenuOpen(false);

  return (
    <>
      <nav className="primary-nav">
        <div className="primary-nav-inner">
          <NavLink
            to="/home"
            className="nav-logo"
            aria-label="Owllet Reading Quest — go to home"
          >
            <img src="/images/owlletLogo.png" alt="" className="nav-logo-img" />
          </NavLink>
          <div className="primary-nav-links">
            <NavLink
              to="/home"
              className={({ isActive }) =>
                isActive ? "nav-link active" : "nav-link"
              }
            >
              Home
            </NavLink>
            <NavLink
              to="/about"
              className={({ isActive }) =>
                isActive ? "nav-link active" : "nav-link"
              }
            >
              About
            </NavLink>
            <NavLink
              to="/help"
              className={({ isActive }) =>
                isActive ? "nav-link active" : "nav-link"
              }
            >
              Help
            </NavLink>
            <NavLink
              to="/contact"
              className={({ isActive }) =>
                isActive ? "nav-link active" : "nav-link"
              }
            >
              Contact
            </NavLink>
            <NavLink
              to="/calendar"
              className={({ isActive }) =>
                isActive
                  ? "nav-link nav-calendar active"
                  : "nav-link nav-calendar"
              }
            >
              <CalendarDays size={14} aria-hidden="true" />
              Calendar
            </NavLink>
            {isAdmin && (
              <span className="nav-admin-group">
                <NavLink
                  to="/admin/users"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Users
                </NavLink>
                <NavLink
                  to="/admin/report"
                  className={({ isActive }) =>
                    isActive ? "nav-link active" : "nav-link"
                  }
                >
                  Report
                </NavLink>
              </span>
            )}
            {session && (
              <NavLink
                to="/vocab/print"
                className={({ isActive }) =>
                  isActive ? "nav-link active" : "nav-link"
                }
              >
                Print Vocabulary
              </NavLink>
            )}
            {session && (
              <button className="nav-link nav-signout" onClick={handleSignOut}>
                Sign Out
              </button>
            )}
          </div>
          <div className="nav-header-right">
            {session && <ThemeSelector />}
            <button
              className="nav-burger-btn"
              onClick={() => setMenuOpen(true)}
              aria-label="Open navigation menu"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile nav drawer */}
      <div
        className={`nav-mobile-overlay${menuOpen ? " open" : ""}`}
        onClick={closeMenu}
        aria-hidden="true"
      />
      <div
        className={`nav-mobile-drawer${menuOpen ? " open" : ""}`}
        role="dialog"
        aria-label="Navigation menu"
      >
        <div className="nav-mobile-header">
          <span className="nav-mobile-title">Menu</span>
          <button
            className="nav-mobile-close"
            onClick={closeMenu}
            aria-label="Close menu"
          >
            <X size={20} />
          </button>
        </div>
        <div className="nav-mobile-links">
          <NavLink
            to="/home"
            className={({ isActive }) =>
              isActive ? "nav-mobile-link active" : "nav-mobile-link"
            }
            onClick={closeMenu}
          >
            Home
          </NavLink>
          <NavLink
            to="/about"
            className={({ isActive }) =>
              isActive ? "nav-mobile-link active" : "nav-mobile-link"
            }
            onClick={closeMenu}
          >
            About
          </NavLink>
          <NavLink
            to="/help"
            className={({ isActive }) =>
              isActive ? "nav-mobile-link active" : "nav-mobile-link"
            }
            onClick={closeMenu}
          >
            Help
          </NavLink>
          <NavLink
            to="/contact"
            className={({ isActive }) =>
              isActive ? "nav-mobile-link active" : "nav-mobile-link"
            }
            onClick={closeMenu}
          >
            Contact
          </NavLink>
          <NavLink
            to="/calendar"
            className={({ isActive }) =>
              isActive ? "nav-mobile-link active" : "nav-mobile-link"
            }
            onClick={closeMenu}
          >
            <CalendarDays
              size={14}
              aria-hidden="true"
              style={{ marginRight: 6, verticalAlign: "middle" }}
            />
            Calendar
          </NavLink>
          {isAdmin && (
            <>
              <NavLink
                to="/admin/users"
                className={({ isActive }) =>
                  isActive ? "nav-mobile-link active" : "nav-mobile-link"
                }
                onClick={closeMenu}
              >
                Users
              </NavLink>
              <NavLink
                to="/admin/report"
                className={({ isActive }) =>
                  isActive ? "nav-mobile-link active" : "nav-mobile-link"
                }
                onClick={closeMenu}
              >
                Report
              </NavLink>
            </>
          )}
          {session && (
            <NavLink
              to="/vocab/print"
              className={({ isActive }) =>
                isActive ? "nav-mobile-link active" : "nav-mobile-link"
              }
              onClick={closeMenu}
            >
              Print Vocabulary
            </NavLink>
          )}
        </div>
        {session && (
          <button
            className="nav-mobile-link nav-mobile-signout"
            onClick={handleSignOut}
          >
            Sign Out
          </button>
        )}
      </div>
    </>
  );
};

/* Hide the site shell (nav, cookie banner, footer) on the standalone landing page */
const AppShell = () => {
  const { pathname } = useLocation();
  if (pathname === "/landing") return null;
  return (
    <>
      <NavBar />
      <CookieBanner />
    </>
  );
};
const AppFooter = () => {
  const { pathname } = useLocation();
  if (pathname === "/landing") return null;
  return <Footer />;
};

export default function App() {
  return (
    <Router>
      <ScrollToTop />
      <a href="#main-content" className="skip-to-main">
        Skip to main content
      </a>
      <AppShell />

      <main id="main-content" className="site-main">
        <ErrorBoundary>
          <Routes>
            {/* auth entry points — no login required */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/projLoginPage" element={<LoginPage />} />
            <Route
              path="/registrationConfirmed"
              element={<RegistrationConfirmedPage />}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/activate/:status" element={<ActivateAccountPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/landing" element={<LandingPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/error" element={<ErrorPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/safeguarding" element={<SafeguardingPage />} />
            <Route path="/accessibility" element={<AccessibilityPage />} />

            {/* every other route requires login */}
            <Route element={<ProtectedRoute />}>
              <Route path="/help" element={<HelpPage />} />
              <Route path="/counter" element={<Counter />} />
              <Route path="/home" element={<HomePage />} />
              <Route path="/selectGameMode" element={<SelectGameModes />} />
              <Route path="/levelSelector" element={<LevelSelector />} />
              <Route
                path="/spotWordGame"
                element={<GameRoute element={<SpotWordGame />} />}
              />
              <Route
                path="/mcq10Game"
                element={<GameRoute element={<Mcq10Game />} />}
              />
              <Route
                path="/comprehensionGame"
                element={<GameRoute element={<ComprehensionGame />} />}
              />
              <Route
                path="/vocabMatchingGame"
                element={<GameRoute element={<VocabularyMatchingGame />} />}
              />
              <Route
                path="/synonymsGame"
                element={<GameRoute element={<SynonymsGame />} />}
              />
              <Route
                path="/antonymsGame"
                element={<GameRoute element={<AntonymsGame />} />}
              />
              <Route
                path="/extremeGame"
                element={<GameRoute element={<ExtremeGame />} />}
              />
              <Route
                path="/rankedGame"
                element={<GameRoute element={<RankedGame />} />}
              />
              <Route
                path="/owlCollectingGacha"
                element={<OwlLotterySystem />}
              />
              <Route
                path="/owlCollectionTest"
                element={<OwlCollectionTest />}
              />
              <Route path="/owlNest" element={<OwlNestPage />} />
              <Route path="/statisticsPage" element={<StatisticsPage />} />
              <Route path="/games/:category" element={<CategoryGamesPage />} />
              <Route path="/bookSelector" element={<BookSelectorPage />} />
              <Route
                path="/bookBasedComprehensionGame"
                element={<GameRoute element={<BookBasedComprehensionGame />} />}
              />
              <Route path="/bookReading" element={<BookReadingPage />} />
              <Route
                path="/chapterReadingPage"
                element={<ChapterReadingPage />}
              />
              <Route path="/my-report" element={<MyReportPage />} />
              <Route path="/admin/users" element={<AdminUsersPage />} />
              <Route
                path="/admin/users/:id/report"
                element={<AdminUserReportPage />}
              />
              <Route path="/admin/report" element={<AdminReportPage />} />
              <Route path="/calendar" element={<CalendarPage />} />
              <Route path="/my-results" element={<ResultsPage />} />
              <Route path="/my-results/:id" element={<ResultDetailPage />} />
              <Route path="/test-modes" element={<TestModesPage />} />
              <Route path="/vocab-test/setup" element={<VocabTestSetup />} />
              <Route path="/vocab-test/game" element={<VocabTestGame />} />
              <Route path="/vocab/print" element={<VocabPrintPage />} />
            </Route>
          </Routes>
        </ErrorBoundary>
      </main>
      <AppFooter />
    </Router>
  );
}
